import { csrfHeaders } from '@hyperio/core/csrf.js';

/**
 * Ciel du moment — composant instancié par le HTML (tag JSP `ui:sky-live`, esprit htmx).
 *
 * L'outil n'a PAS de calcul propre : il réutilise directement l'outil « thème astral » côté client.
 * Sur [data-sky-live] : lit le lieu (lat/lon/timezone remplis par l'autocomplétion de ville) et une
 * date/heure OPTIONNELLE (vide = maintenant). POST (form-urlencoded, Accept JSON) à l'outil thème
 * astral — le serveur interprète l'heure LOCALE au fuseau du lieu (DST correct) — puis dessine la
 * roue avec le renderer SkyChart à partir du thème renvoyé.
 */
import { SkyChart } from '../sky-chart/core/SkyChart.js';


/** Maintenant (moins 1 min pour la contrainte @Past serveur) exprimé en heure locale du fuseau `tz`. */
function nowInTimezone(tz) {
    const now = new Date(Date.now() - 60000);
    const format = (options) => new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC', hour12: false, ...options });
    const parts = format({
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(now);
    const get = (type) => (parts.find((p) => p.type === type) || {}).value;
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Affiche l'heure locale saisie (ses composantes telles quelles) + le fuseau du lieu. */
function formatWhen(localDateTime, tz) {
    const date = new Date(localDateTime);
    if (Number.isNaN(date.getTime())) return localDateTime;
    const base = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        dateStyle: 'full', timeStyle: 'short',
    }).format(date);
    return tz ? `${base} (${tz})` : base;
}

function attach(root) {
    const computeUrl = root.dataset.skyLiveCompute;
    if (!computeUrl) return;

    const form = root.querySelector('form');
    const trigger = root.querySelector('[data-sky-live-trigger]');
    const chartBox = root.querySelector('[data-sky-live-chart]');
    const status = root.querySelector('[data-sky-live-status]');

    const field = (name) => form.querySelector(`[name="${name}"]`);
    const say = (text) => { status.textContent = text; status.hidden = false; };

    function render(chartData, dateTime, timezone) {
        chartBox.hidden = false;
        // SkyChart pose un ResizeObserver et n'expose pas de destroy → on repart d'un canvas neuf.
        chartBox.replaceChildren(document.createElement('canvas'));
        new SkyChart(chartBox.querySelector('canvas'), chartData);
        say(`${root.dataset.skyLiveAt || ''} ${formatWhen(dateTime, timezone)}`.trim());
    }

    function compute() {
        const latitude = field('latitude').value;
        const longitude = field('longitude').value;
        if (!latitude || !longitude) {
            say(root.dataset.skyLivePickCity || '');
            return;
        }
        const timezone = field('timezone').value;
        // Date/heure saisie (heure locale du lieu) ; vide = maintenant, au fuseau du lieu.
        const dateTime = field('dateTime').value || nowInTimezone(timezone);

        const headers = { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' };

        trigger.disabled = true;
        say('…');
        fetch(computeUrl, {
            method: 'POST',
            headers: csrfHeaders(computeUrl, headers),
            body: new URLSearchParams({ dateTime, timezone, latitude, longitude }),
        })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
            .then((data) => {
                const chartData = data.chartJson ? JSON.parse(data.chartJson) : data.result;
                if (!chartData) throw new Error('réponse sans thème');
                render(chartData, dateTime, timezone);
            })
            .catch(() => say(root.dataset.skyLiveError || 'Erreur'))
            .finally(() => { trigger.disabled = false; });
    }

    trigger.addEventListener('click', compute);
    field('city').addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-sky-live]').forEach(attach);
});
